/**
 * iOS Safari Crash Recovery
 * 
 * This MUST be the first code that runs - before any imports that might fail.
 * iOS Safari crashes with "A problem repeatedly occurred" when:
 * 1. Service worker is corrupted or stuck
 * 2. Cache state is invalid
 * 3. Heavy module parsing causes memory pressure
 * 
 * This recovery script clears all problematic state before the app loads.
 */

// IIFE that runs immediately to recover from iOS Safari crash loop
(function iosRecovery() {
  try {
    // Check if we're on iOS
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    const isIOS = /ipad|iphone|ipod/i.test(ua);
    
    if (!isIOS) return;
    
    // Check if we've already attempted recovery this session
    const recoveryKey = 'ios-sw-recovery-v1';
    const hasRecovered = sessionStorage.getItem(recoveryKey);
    
    if (hasRecovered) return;
    
    // Mark that we're attempting recovery
    sessionStorage.setItem(recoveryKey, Date.now().toString());
    
    // Clear all caches immediately
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name).catch(() => {});
        });
      }).catch(() => {});
    }
    
    // Unregister any stuck service workers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(reg => {
          // If there's a waiting or installing worker, it might be stuck
          if (reg.waiting || reg.installing) {
            reg.unregister().catch(() => {});
          }
        });
      }).catch(() => {});
    }
  } catch (e) {
    // Silently fail - don't prevent app from loading
  }
})();

// Suppress console.log in production — keep console.error and console.warn
if (import.meta.env.PROD) {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
}

import { StrictMode } from 'react'

import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import App from './App.tsx'
import { installGlobalErrorReporting } from './lib/errorReporting'
import './index.css'
import './i18n'

console.log('🚀 main.tsx: Starting application');

// Detect iOS safely - this runs after the recovery IIFE
let isIOS = false;
try {
  if (typeof navigator !== 'undefined' && navigator.userAgent) {
    isIOS = /ipad|iphone|ipod/i.test(navigator.userAgent);
  }
} catch {
  // Silently fail - assume not iOS
}

// Register service worker with iOS-safe handling
// For iOS, we use a much longer delay and skip registration entirely if there were recent issues
if ('serviceWorker' in navigator) {
  const registerDelay = isIOS ? 5000 : 0; // 5 second delay for iOS
  
  // Check if iOS had issues recently (within last hour)
  const lastRecovery = sessionStorage.getItem('ios-sw-recovery-v1');
  const recentRecovery = lastRecovery && (Date.now() - parseInt(lastRecovery)) < 3600000;
  
  if (isIOS && recentRecovery) {
    console.log('📦 PWA: Skipping SW registration on iOS after recent recovery');
  } else {
    setTimeout(() => {
      registerSW({
        onNeedRefresh() {
          console.log('📦 PWA: New content available - auto-reloading...');
          // Auto-reload when new content is available
          window.location.reload();
        },
        onOfflineReady() {
          console.log('✅ PWA: App ready for offline use');
        },
        onRegistered(registration) {
          console.log('✅ PWA: Service worker registered', registration);
          // Set up periodic update checks (every 5 minutes)
          if (registration) {
            setInterval(() => {
              registration.update().catch(console.error);
            }, 5 * 60 * 1000);
          }
        },
        onRegisterError(error) {
          console.error('❌ PWA: Service worker registration failed', error);
          // On iOS, clear caches if SW registration fails
          if (isIOS && 'caches' in window) {
            caches.keys().then(names => {
              names.forEach(name => caches.delete(name));
            }).catch(() => {});
          }
        },
      });
    }, registerDelay);
  }
}

// Proactive error capture: global handlers + breadcrumbs + batched reporting
installGlobalErrorReporting();

// Local console visibility (reporting itself is handled above)
window.addEventListener('error', (event) => {
  console.error('💥 Global error:', event.error);
});

window.addEventListener('unhandledrejection', (event) => {
  console.error('💥 Unhandled promise rejection:', event.reason);
});

// Helper function for clearing cache manually
const clearCacheAndReload = async () => {
  try {
    // Clear all caches
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    }
    
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(reg => reg.unregister()));
    }
    
    // Clear session recovery flag
    sessionStorage.removeItem('ios-sw-recovery-v1');
    
    // Reload
    window.location.reload();
  } catch (e) {
    // Force reload even if cleanup fails
    window.location.reload();
  }
};

// Make available globally for error UI
(window as any).clearCacheAndReload = clearCacheAndReload;

// Ensure the DOM is fully loaded before mounting the app
const rootElement = document.getElementById("root");
if (!rootElement) {
  console.error('❌ main.tsx: Failed to find the root element');
  throw new Error("Failed to find the root element");
}

console.log('✅ main.tsx: Root element found, creating React root');

try {
  // Create root only once and mount the app
  const root = createRoot(rootElement);
  console.log('🎯 main.tsx: Rendering App component');

  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );

  console.log('✅ main.tsx: App rendered successfully');
} catch (error) {
  console.error('❌ main.tsx: Error rendering app:', error);
  
  // Fallback error display with Clear Cache button for iOS users
  rootElement.innerHTML = `
    <div style="
      min-height: 100vh; 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      font-family: system-ui, -apple-system, sans-serif;
      background: #0f172a;
      padding: 20px;
    ">
      <div style="
        max-width: 400px; 
        padding: 24px; 
        background: #1e293b; 
        border-radius: 12px; 
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
        text-align: center;
      ">
        <div style="font-size: 48px; margin-bottom: 16px;">🎾</div>
        <h1 style="color: #f87171; margin-bottom: 16px; font-size: 20px;">Application Error</h1>
        <p style="color: #94a3b8; margin-bottom: 20px; font-size: 14px; line-height: 1.5;">
          There was an error starting LuvALL. This can happen if cached data is corrupted.
        </p>
        <div style="display: flex; flex-direction: column; gap: 12px;">
          <button 
            onclick="window.clearCacheAndReload ? window.clearCacheAndReload() : window.location.reload()" 
            style="
              background: #4ade80; 
              color: #0f172a; 
              border: none; 
              padding: 12px 20px; 
              border-radius: 8px; 
              cursor: pointer;
              font-weight: 600;
              font-size: 14px;
            "
          >
            Clear Cache & Reload
          </button>
          <button 
            onclick="window.location.reload()" 
            style="
              background: transparent; 
              color: #94a3b8; 
              border: 1px solid #334155; 
              padding: 10px 16px; 
              border-radius: 8px; 
              cursor: pointer;
              font-size: 13px;
            "
          >
            Try Simple Refresh
          </button>
        </div>
      </div>
    </div>
  `;
}
